import React, { useEffect, useState } from "react"
import {
  Divider,
  Box,
  Paper,
  Typography,
  Breadcrumbs,
  ListItem,
  ListItemText,
  List,
  Link,
  useTheme,
  useMediaQuery,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/styles"
import { NavLink } from "react-router-dom"
import { KNOWLEDGE_CONTENT_TYPE } from "../../data"
import { toId, useTraining } from "../../utils"
import { AuthorTools, Avatar, TimeAgo, SubNavigation, AdornmentIcon, TooltipIcon } from ".."
import { KnowledgeActionCard } from "../ActionCards"
import { CopyLinkButton } from "../Buttons"
import { RowBox } from "../Boxes"
import { KnowledgeArticleContent } from "./KnowledgeArticleContent"
import { TrainingArticleFooter, TrainingArticleHeader } from "../Training"

const useStyles = makeStyles((theme) => ({
  root: {
    color: theme.palette.text.main,
    flex: 1,
    // marginBottom: theme.spacing(3),
    cursor: "pointer",
  },
  paper: {
    "&:hover": theme.palette.hoverCard,
    "&:focus": theme.palette.hoverCard,
  },
  avatar: {
    backgroundColor: theme.palette.primary.main,
  },
  cardTitle: {
    fontSize: 20,
    fontWeight: "600",
    lineHeight: "28px",
    marginBottom: theme.spacing(1),
    color: theme.palette.text.main,
  },
  title: {
    fontSize: 24,
    fontWeight: "600",
    lineHeight: "30px",
    marginBottom: 11,
    color: theme.palette.text.main,
  },
  description: {
    color: theme.palette.text.main,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  author: {
    fontSize: 14,
    lineHeight: "16px",
    color: theme.palette.text.light,
  },
  footer: {
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
  },
  lastUpdated: {
    color: theme.palette.text.light,
  },
  listItem: {
    padding: 0,
    color: theme.palette.primary.main,
    fontSize: 16,
    marginTop: 4,
    marginBottom: 4,
    fontWeight: "600",
    "&:hover": {
      background: "#ecf4ff",
      borderRadius: "4px",
      textDecoration: "none",
    },
    "&:focus": {
      background: "#ecf4ff",
      borderRadius: "4px",
      textDecoration: "none",
    },
  },
  listItemAvatar: {
    minWidth: 38,
  },
  listItemText: {
    fontSize: 16,
    fontWeight: "600 !important",
    textDecoration: "none",
  },
  displayContent: {
    fontSize: 16,
    lineHeight: "24px",
    whiteSpace: "pre-wrap",
    marginBottom: 40,
  },
  displayHeading: {
    fontSize: 20,
    fontWeight: "600",
    color: theme.palette.kb.heading,
  },
  submenu: {
    padding: `${theme.spacing(2)}px !important`,
  },
  submenuTitle: {
    fontSize: 24,
    fontWeight: "600",
    lineHeight: "30px",
    marginBottom: theme.spacing(1),
  },
  by: {
    color: theme.palette.text.light,
  },
  trainingIcon: {
    display: "flex",
    flexDirection: "row",
  },
}))

const KnowledgeArticle = ({ knowledge, variant, onEdit, onDelete }) => {
  const theme = useTheme()
  const fullWidth = useMediaQuery(theme.breakpoints.down("sm"))
  const { getActiveTrainingForKnowledge, modules: trainingModules } = useTraining()
  const [hasTrainingCourse, setHasTrainingCourse] = useState(false)
  const classes = useStyles()
  const { id, title, category, content, groups, locations, author, createdAt, updatedAt } = knowledge

  const xs = useMediaQuery(theme.breakpoints.down("xs"))

  const training = getActiveTrainingForKnowledge(knowledge)
  const hasUserTraining = Boolean(training)

  useEffect(() => {
    if (!knowledge || !trainingModules?.length) {
      return
    }
    setHasTrainingCourse(
      trainingModules.some((trainingModule) => trainingModule.knowledges.some((item) => toId(item) === toId(knowledge)))
    )
  }, [trainingModules, knowledge])

  const scrollTo = (event, to) => {
    event.preventDefault()
    const offset = document.querySelector("header").clientHeight + 20
    const pos = document.getElementById(to).getBoundingClientRect().top + document.documentElement.scrollTop
    window.scrollTo({
      top: pos - offset,
      behavior: "smooth",
    })
  }

  const ArticleAuthorTools = () => (
    <AuthorTools
      item={knowledge}
      author={knowledge.author.id}
      subject="article"
      admins={{ edit: "knowledge_update_all", delete: "knowledge_delete_all" }}
      onEdit={onEdit}
      onDelete={onDelete}
    />
  )

  const hasGroupsOrLocations = Boolean(groups?.length) || Boolean(locations?.length)

  if (variant === "submenu") {
    return (
      <SubNavigation className={classes.submenu} mr={fullWidth ? 0 : 6}>
        <Typography variant="h2" className={classes.submenuTitle}>
          Contents
        </Typography>
        <List>
          {content
            .filter(
              (c) =>
                c.heading &&
                ![
                  KNOWLEDGE_CONTENT_TYPE.NOTE,
                  KNOWLEDGE_CONTENT_TYPE.NOTE_DANGER,
                  KNOWLEDGE_CONTENT_TYPE.NOTE_IMPORTANT,
                ].includes(c.type)
            )
            .map((c) => (
              <ListItem
                key={toId(c)}
                component={Link}
                href={`#${c.id}`}
                onClick={(event) => scrollTo(event, toId(c))}
                classes={{ root: classes.listItem }}
              >
                <ListItemText>
                  <Typography className={classes.listItemText}>{c.heading}</Typography>
                </ListItemText>
              </ListItem>
            ))}
        </List>
      </SubNavigation>
    )
  }

  if (variant === "full") {
    return (
      <Box data-cy="KnowledgeArticle-full">
        <Box mb={3}>
          <Box display="flex" flexDirection="row" alignItems="flex-start">
            <Box>
              <Typography variant="h3" className={classes.title}>
                {title}
              </Typography>
            </Box>
            <Box ml="auto" display="flex" flexDirection="row">
              <CopyLinkButton />
              <ArticleAuthorTools />
            </Box>
          </Box>
        </Box>
        {!hasUserTraining && (
          <Box mb={xs ? 2 : 4} data-cy="KnowledgeArticle-breadcrumbs">
            <Breadcrumbs>
              <NavLink to="/knowledge">Knowledge Base</NavLink>
              <NavLink to={`/knowledge/category/${category.id}`}>{category.name}</NavLink>
              <Box>{title}</Box>
            </Breadcrumbs>
          </Box>
        )}
        {!hasUserTraining && (
          <Box mb={3}>
            <Paper elevation={3} className={classes.paper}>
              <RowBox p={2} alignItems="center">
                <Avatar {...author} />
                <Box ml={1} className={classes.by}>
                  By {author.firstName} {author.lastName} | <TimeAgo date={updatedAt || createdAt} />{" "}
                </Box>
                {(hasGroupsOrLocations || hasTrainingCourse) && (
                  <Box ml="auto" mr={1}>
                    {hasGroupsOrLocations && (
                      <Box mr={hasTrainingCourse ? 1 : 0}>
                        <TooltipIcon
                          name="restricted"
                          refClassName={classes.trainingIcon}
                          tooltip="Restricted to locations and/or areas of work"
                        />
                      </Box>
                    )}
                    {hasTrainingCourse && (
                      <TooltipIcon
                        name="training"
                        refClassName={classes.trainingIcon}
                        tooltip="Article is used as training"
                      />
                    )}
                  </Box>
                )}
              </RowBox>
            </Paper>
          </Box>
        )}
        {hasUserTraining && (
          <Box mb={3}>
            <TrainingArticleHeader {...training} />
          </Box>
        )}
        {content.map((c) => (
          <KnowledgeArticleContent key={c.id} id={c.id} content={c} />
        ))}
        {hasUserTraining && (
          <Box mb={10}>
            <TrainingArticleFooter {...training} />
          </Box>
        )}
      </Box>
    )
  }

  if (variant === "cardlink") {
    return <KnowledgeActionCard knowledge={knowledge} />
  }

  return (
    <Box className={classes.root} mb={xs ? 2 : 3}>
      <Paper elevation={3} className={classes.paper}>
        <Box p={2} display="flex" flexDirection="row">
          <Box component={NavLink} to={`/knowledge/article/${id}`} display="flex" flexDirection="row" flexGrow={1}>
            <Box>
              <Typography variant="h4" className={classes.cardTitle}>
                {title}
              </Typography>
              <Box className={classes.author}>
                {author.firstName} {author.lastName}
              </Box>
            </Box>
          </Box>
          <Box ml="auto">
            <ArticleAuthorTools />
          </Box>
        </Box>
        <Divider />

        <RowBox className={classes.footer} alignItems="center" component={NavLink} to={`/knowledge/article/${id}`}>
          {hasGroupsOrLocations && (
            <Box mr={1}>
              <AdornmentIcon name="restricted" tooltip="Restricted to locations and/or areas of work" />
            </Box>
          )}
          {hasTrainingCourse && (
            <Box>
              <AdornmentIcon name="training" tooltip="Article is used as training" />
            </Box>
          )}
          <Box flexGrow={1} />
          <RowBox className={classes.lastUpdated}>
            {updatedAt !== createdAt && (
              <Box mr={1}>
                Updated <TimeAgo date={updatedAt} />
              </Box>
            )}
            {updatedAt === createdAt && (
              <Box mr={1}>
                Created <TimeAgo date={createdAt} />
              </Box>
            )}
            <Avatar {...author} />
          </RowBox>
        </RowBox>
      </Paper>
    </Box>
  )
}

export { KnowledgeArticle }
